import DashboardNavigationBar from '@/components/dashboard/navigation-bar/dashboard-navigation-bar';
import MainContent from '@/components/main-layout/components/main-content/main-content';
import TabItem from '@/components/tab-item/tab-item';
import { useSticky } from '@/hooks/use-sticky';
import { useGetNavbarHeight } from '@/queries/layout.queries';
import { Box, Divider, Tabs, Text } from '@cmpsr/components';
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import useAccountController from './account.controller';
import AccountTab from './components/account-tab/account-tab';
import HeroAccount from './components/hero-account/hero-account';
import RiskAssessmentTab from './components/risk-assessment-tab/risk-assessment-tab';

function AccountPage() {
  const {
    name,
    email,
    cardLast4Digits,
    assessmentProgress,
    defaultTab,
    type,
    cancelAtPeriodEnd,
    usdPrice,
    renewalDate,
    questionnaireStatus,
    iDontKnowAnswers,
    answeredQuestions,
    notificationsPreferences,
    paymentFunding,
    isVipAccess,
  } = useAccountController();
  const { data: navBarSize } = useGetNavbarHeight();
  const { ref, isSticky } = useSticky(navBarSize);
  const [searchParams, setSearchParams] = useSearchParams();
  
  const currentTab = searchParams.get('tab') === 'risk' ? 1 : 0;
  
  const handleTabChange = (index: number) => {
    const newParams = new URLSearchParams(searchParams);
    if (index === 1) {
      newParams.set('tab', 'risk');
    } else {
      newParams.delete('tab');
    }
    setSearchParams(newParams);
  };
  // When using useSticky make sure any child components are wrapped in React.memo at export

  return (
    <>
      <DashboardNavigationBar />
      <MainContent data-testid="account-page">
        <HeroAccount username={name} />
        <Tabs
          variant="line"
          px={{
            base: 'spacer-4',
            md: 'spacer-8',
            lg: 'spacer-16',
            xl: 'spacer-24',
            xxl: 'spacer-32',
          }}
          maxWidth="1440px"
          marginX="auto"
          isLazy
          index={currentTab}
          onChange={handleTabChange}
        >
          <Box ref={ref} pt={isSticky ? '40px' : 'inherit'} />
          <Box
            pt={{ base: 8, md: 10, lg: 12 }}
            {...(isSticky
              ? {
                  position: 'fixed',
                  top: `${navBarSize}px`,
                  px: {
                    base: 'spacer-4',
                    md: 'spacer-8',
                    lg: 'spacer-16',
                    xl: 'spacer-24',
                    xxl: 'spacer-32',
                  },
                  left: 0,
                  right: 0,
                  width: '100%',
                  zIndex: 1,
                  bgColor: 'background-page',
                  boxShadow: 'elevation-interactive',
                }
              : {
                  position: 'unset',
                })}
          >
            <Tabs.List maxWidth="1184px" marginX="auto">
              <TabItem>
                <Text
                  variant="text-body-meta-medium"
                  textTransform="uppercase"
                  whiteSpace="nowrap"
                >
                  Account
                </Text>
              </TabItem>
              <TabItem>
                <Text
                  variant="text-body-meta-medium"
                  textTransform="uppercase"
                  whiteSpace="nowrap"
                >
                  Health Data
                </Text>
              </TabItem>
            </Tabs.List>
          </Box>
          <Divider display={isSticky ? 'none' : 'auto'} />
          <Tabs.Panels>
            <Tabs.Panel p="0">
              <AccountTab
                name={name}
                email={email}
                type={type}
                usdPrice={usdPrice}
                renewalDate={renewalDate}
                assessmentProgress={assessmentProgress}
                cancelAtPeriodEnd={cancelAtPeriodEnd}
                questionnaireStatus={questionnaireStatus}
                iDontKnowAnswers={iDontKnowAnswers}
                totalAnsweredQuestions={answeredQuestions}
                notificationsPreferences={notificationsPreferences}
                cardLast4Digits={cardLast4Digits}
                paymentFunding={paymentFunding}
                isVipAccess={isVipAccess}
              />
            </Tabs.Panel>
            <Tabs.Panel p="0">
              <RiskAssessmentTab
                assessmentProgress={assessmentProgress}
                questionnaireStatus={questionnaireStatus}
                iDontKnowAnswers={iDontKnowAnswers}
                totalAnsweredQuestions={answeredQuestions}
              />
            </Tabs.Panel>
          </Tabs.Panels>
        </Tabs>
      </MainContent>
    </>
  );
}

export default React.memo(AccountPage);
